// @flow
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toastr } from 'react-redux-toastr';
import { isEqual } from 'lodash';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useHistory from '../../../hooks/useHistory';
import useIntl from '../../../hooks/useIntl';
import { generateStandardFilename } from '@fortress-technology-solutions/fortress-component-library/utils';
import { getURLProgramName } from '../../../utils/affordable';
import {
  buildCertNavSearch,
  getCertTabFromLocation,
  getQualificationFormDisabledState,
} from '../../QualificationForms/utils';
import { shouldUseHudCompletedTabFormReads } from '../../GeneralAffordable/isHudCompletedTab';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import AffordableQualificationService from '../../../services/affordableQualificationService';
import HouseholdService from '../../../services/householdService';
import useAllStates from '../../LeaseApplicationForm/hooks/data-fetching/useAllStates';
import useAllEmploymentStatuses from '../../LeaseApplicationForm/hooks/data-fetching/useAllEmploymentStatuses';
import { AppContext } from '../../App/context';
import buildRecertSchema from '../schema';
import { toFormValues, toPayload } from '../mapping';
import { hasRequiredAnswers, isSignedCopyChecked } from '../completion';
import { revealBlockingAnswers, validateForSave } from '../formValidation';
import { formatLastSaved } from '../lastSaved';
import messages from '../messages';
import type { RecertApplicationRouteProps } from '../types';

const QUERY_OPTIONS = { refetchOnWindowFocus: false };

// The API rejects with a string, an Error or a body; a resolved body can also carry the error.
const errorMessage = (error: any): ?string =>
  typeof error === 'string' ? error : error?.message ?? error?.error;

export default function useRecertApplicationForm(
  match: $PropertyType<RecertApplicationRouteProps, 'match'>,
  location: $PropertyType<RecertApplicationRouteProps, 'location'>,
) {
  const intl = useIntl();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { selectedProperty } = useContext(AppContext);
  const {
    affordableQualificationId,
    householdId,
    applicantId,
    residentId,
    programName,
  } = match.params;
  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id ?? match.params.propertyId;
  const enabled = Boolean(organizationId && propertyId);
  const loadErrorText = intl.formatMessage(messages.loadError);

  const { states } = useAllStates();
  const { employmentStatuses, isError: hasStatusesError } =
    useAllEmploymentStatuses();

  const flags = useFlags();
  const hasModifyReopenCertPermission = usePermissionsHook({
    scope: ['hud-qualification-modify-reopen-cert'],
  });
  const legacyProgramName = programName.replace(/_/g, '/');
  // On the HUD Completed tab the legacy form reads the completed snapshot, not the live row.
  const isHudCompletedTab = shouldUseHudCompletedTabFormReads({
    flags,
    programName: legacyProgramName,
    certTab: getCertTabFromLocation(location),
  });

  const formQueryKey = [
    'recert-application-form',
    propertyId,
    affordableQualificationId,
    applicantId,
    isHudCompletedTab,
  ];
  const formQuery = useQuery(
    formQueryKey,
    () =>
      new AffordableQualificationService().getRecertForm(
        organizationId,
        propertyId,
        applicantId,
        affordableQualificationId,
        { isHudCompletedTab, affordableQualificationId },
      ),
    {
      ...QUERY_OPTIONS,
      enabled,
      onError: () => toastr.error('Error', loadErrorText),
    },
  );
  const aqQuery = useQuery(
    ['recert-application-qualification', propertyId, affordableQualificationId],
    () =>
      new AffordableQualificationService().getById(
        organizationId,
        propertyId,
        affordableQualificationId,
      ),
    {
      ...QUERY_OPTIONS,
      enabled,
      onError: () => toastr.error('Error', loadErrorText),
    },
  );
  const primaryQuery = useQuery(
    ['recert-application-is-primary', propertyId, householdId, applicantId],
    async () => {
      const response = await new HouseholdService().checkIfApplicantIsPrimary(
        organizationId,
        propertyId,
        householdId,
        applicantId,
      );
      // The API answers 200 with an error body when the check fails; that is not "not primary".
      if (response?.error) {
        throw new Error(errorMessage(response) ?? undefined);
      }
      return response;
    },
    {
      ...QUERY_OPTIONS,
      enabled,
      onError: () => toastr.error('Error', loadErrorText),
    },
  );

  // A body with an error stands for a recert that has nothing saved yet.
  const saved = formQuery.data?.error ? null : formQuery.data;
  const isPrimary = primaryQuery.data === true;
  const isLoading =
    formQuery.isLoading || primaryQuery.isLoading || aqQuery.isLoading;
  const hasLoadError =
    formQuery.isError || primaryQuery.isError || aqQuery.isError;

  const { isDisabled: isLocked } = getQualificationFormDisabledState({
    affordableQualification: aqQuery.data ?? {},
    flags,
    programName: legacyProgramName,
    useHudCompletedTabSnapshot: isHudCompletedTab,
    hasModifyReopenCertPermission,
  });

  // A failed reload keeps the last data it received; going by isSuccess there would drop the
  // baseline and make an edited form look clean.
  const hasFormData = formQuery.data !== undefined;
  const formValues = useMemo(
    () => (hasFormData ? toFormValues(saved) : undefined),
    [hasFormData, saved],
  );

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: {},
    values: formValues,
    mode: 'onChange',
  });

  const asyncOptions = useMemo(
    () => ({
      employmentStatus: employmentStatuses.map(({ id, name, value, text }) => ({
        value: value ?? id,
        text: text ?? name,
        label: text ?? name,
      })),
      currentJobState: states,
      secondJobState: states,
      previousJobState: states,
    }),
    [employmentStatuses, states],
  );

  // The id of the Not Employed status, which empties and disables the current employers.
  const notEmployedStatusId = asyncOptions.employmentStatus.find(
    ({ text }) => text === 'Not Employed',
  )?.value;
  // Only an Employed applicant has to describe the current employer.
  const employedStatusId = asyncOptions.employmentStatus.find(
    ({ text }) => text === 'Employed',
  )?.value;

  // The statuses start empty, so an empty list cannot tell a failed lookup from a pending one.
  const hasUnusableStatuses =
    hasStatusesError ||
    (asyncOptions.employmentStatus.length > 0 && !employedStatusId);
  useEffect(() => {
    if (hasUnusableStatuses) toastr.error('Error', loadErrorText);
  }, [hasUnusableStatuses, loadErrorText]);

  const { watch, setValue, getValues } = ReactHookFormProps;
  const payloadOptions = { isPrimary, loaded: saved, employedStatusId };
  // The form is compared to the saved row as payloads, so values the inputs rewrite on load
  // (masks, registration defaults, the 'on'/'' a checkbox may hold) do not count as a change.
  const comparablePayload = (values) => {
    const { hasSignedCopy, ...payload } = toPayload(values, payloadOptions);
    return { ...payload, hasSignedCopy: Boolean(hasSignedCopy) };
  };
  const isDirty =
    formValues !== undefined &&
    !isEqual(comparablePayload(watch()), comparablePayload(formValues));
  // The form library re-reads a field's rules only when the rules object or the field's
  // disabled/hidden state changes, not when its watchFields change. Rebuilding the schema on the
  // answers that decide what is required keeps the conditional "Required" marks up to date.
  const [isStudent, studentType, employmentStatus] = watch([
    'isStudent',
    'studentType',
    'employmentStatus',
  ]);

  const schema = useMemo(
    () =>
      buildRecertSchema({
        isPrimary,
        isLocked,
        notEmployedStatusId,
        employedStatusId,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isPrimary,
      isLocked,
      notEmployedStatusId,
      employedStatusId,
      isStudent,
      studentType,
      employmentStatus,
    ],
  );

  const blockingAnswersText = intl.formatMessage(messages.blockingAnswers);
  const hadRequiredAnswers = useRef(false);
  useEffect(() => {
    // No watch event fires when the statuses arrive after the values, so the starting point is
    // taken from the values already in the form.
    hadRequiredAnswers.current = hasRequiredAnswers(getValues(), {
      employedStatusId,
    });
    const subscription = watch((values, { name, type }) => {
      const hasAnswers = hasRequiredAnswers(values, { employedStatusId });
      const isChecked = isSignedCopyChecked(values.hasSignedCopy);
      if (
        name === 'hasSignedCopy' &&
        type === 'change' &&
        isChecked &&
        !hasAnswers
      ) {
        // The box cannot be checked over missing answers: it is undone and they are pointed out.
        setValue('hasSignedCopy', false);
        revealBlockingAnswers({
          ...ReactHookFormProps,
          onInvalid: () => toastr.error('Error', blockingAnswersText),
        });
      } else if (hadRequiredAnswers.current && !hasAnswers && isChecked) {
        // Clearing a required answer unchecks the box. Only on that transition: field registration
        // and input masks also fire watch on load, and a box saved as checked over missing answers
        // is shown as saved.
        setValue('hasSignedCopy', false);
      }
      hadRequiredAnswers.current = hasAnswers;
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    setValue,
    getValues,
    employedStatusId,
    ReactHookFormProps,
    blockingAnswersText,
  ]);

  const close = () => {
    history.push(
      `/property/${propertyId}/resident/${residentId}/?tab=${getURLProgramName(
        programName,
      )}${buildCertNavSearch(location)}`,
    );
  };

  const { mutateAsync: saveProgress, isLoading: isSaving } = useMutation(
    async () => {
      // Same as the legacy form: a value the form refuses is shown on its field and not saved.
      if (!(await validateForSave(ReactHookFormProps))) {
        throw new Error(intl.formatMessage(messages.invalidForm));
      }
      const payload = toPayload(ReactHookFormProps.getValues(), payloadOptions);
      const result = await new AffordableQualificationService().saveRecertForm(
        payload,
        organizationId,
        propertyId,
        applicantId,
        affordableQualificationId,
      );
      if (result?.error || result?.statusCode >= 400) {
        throw new Error(errorMessage(result) ?? undefined);
      }
      // Resolves once the saved row is back in the form, so a close or print that follows
      // acts on what was saved.
      await queryClient.invalidateQueries(formQueryKey);
    },
    {
      onSuccess: () =>
        toastr.success('Success', intl.formatMessage(messages.saveSuccess)),
      onError: (error) =>
        toastr.error(
          'Error',
          errorMessage(error) || intl.formatMessage(messages.saveError),
        ),
    },
  );

  // The toasts already said why a save did not go through; callers only need the outcome.
  const save = async (): Promise<boolean> => {
    // Completion depends on knowing which status is Employed; the confirmations save through here
    // too, so a save without it would send isComplete false and undo a completed recert.
    if (!employedStatusId) {
      toastr.error('Error', loadErrorText);
      return false;
    }
    try {
      await saveProgress();
      return true;
    } catch (error) {
      return false;
    }
  };

  const { mutate: printSaved, isLoading: isPrinting } = useMutation(
    async () => {
      const fileName = generateStandardFilename({
        type: 'recert-application',
        descriptor: `${selectedProperty?.name ?? ''} ${programName}`.trim(),
        extension: 'pdf',
      });
      const result =
        await new AffordableQualificationService().downloadRecertificationForm(
          organizationId,
          propertyId,
          applicantId,
          affordableQualificationId,
          fileName,
        );
      // A failed download resolves with a body when the API answers with JSON.
      if (result?.__noContent || (!(result instanceof Blob) && result?.error)) {
        throw new Error(errorMessage(result) ?? undefined);
      }
    },
    {
      onError: (error) =>
        toastr.error(
          'Error',
          errorMessage(error) || intl.formatMessage(messages.printError),
        ),
    },
  );

  // Which confirmation is open: the user asked to close or print over unsaved changes.
  const [pendingAction, setPendingAction] =
    useState<?('close' | 'print')>(null);
  const runAction = (action) => (action === 'close' ? close() : printSaved());
  const hasUnsavedChanges = isDirty && !isLocked;

  const requestClose = () =>
    hasUnsavedChanges ? setPendingAction('close') : close();
  const requestPrint = () =>
    hasUnsavedChanges ? setPendingAction('print') : printSaved();

  // Close stays available while a save runs; a second save started from the confirmation could
  // finish first and be overwritten by the earlier one.
  const isWriting = isSaving || formQuery.isFetching;
  const confirmSaveAnd = async () => {
    if (isWriting) return;
    const action = pendingAction;
    setPendingAction(null);
    if (await save()) runAction(action);
  };
  const proceedWithout = () => {
    const action = pendingAction;
    setPendingAction(null);
    runAction(action);
  };
  const dismiss = () => setPendingAction(null);

  const confirmation =
    pendingAction === null
      ? null
      : {
          kind: pendingAction,
          title: intl.formatMessage(
            pendingAction === 'close'
              ? messages.closeConfirmTitle
              : messages.printConfirmTitle,
          ),
          body: intl.formatMessage(
            pendingAction === 'close'
              ? messages.closeConfirmBody
              : messages.printConfirmBody,
          ),
          primaryLabel: intl.formatMessage(
            pendingAction === 'close'
              ? messages.saveAndClose
              : messages.saveAndPrint,
          ),
          secondaryLabel: intl.formatMessage(
            pendingAction === 'close'
              ? messages.closeWithoutSaving
              : messages.printAnyway,
          ),
          onPrimary: confirmSaveAnd,
          isPrimaryDisabled: isWriting,
          onSecondary: proceedWithout,
          onDismiss: dismiss,
        };

  const isBusy = isSaving || isPrinting || formQuery.isFetching;
  const hasSavedRow = Boolean(saved?.id);

  return {
    title: intl.formatMessage(messages.title),
    propertyName: selectedProperty?.name,
    closeLabel: intl.formatMessage(messages.close),
    saveLabel: intl.formatMessage(messages.save),
    printLabel: intl.formatMessage(messages.print),
    canPrint: enabled && !isLoading && !hasLoadError && !isBusy,
    isPrinting,
    ReactHookFormProps,
    schema,
    asyncOptions,
    isLoading,
    // Nothing to call saved, and no time to show, until the first save.
    saveStatus:
      !formValues || isLocked
        ? null
        : isDirty
        ? 'unsaved'
        : hasSavedRow
        ? 'saved'
        : null,
    saveStatusLabel: intl.formatMessage(
      isDirty ? messages.unsavedChanges : messages.saved,
    ),
    lastSavedText: isLocked ? null : formatLastSaved(saved?.updatedAt, intl),
    isSaving,
    // Completion depends on knowing which status is Employed; saving without it would send
    // isComplete false and undo a completed recert.
    canSave:
      enabled &&
      !isLoading &&
      !hasLoadError &&
      !isLocked &&
      Boolean(employedStatusId) &&
      isDirty &&
      !isBusy,
    save,
    requestClose,
    requestPrint,
    confirmation,
    isReadOnly: isLocked,
  };
}
